<template>
    <v-card class="investment-calculator elevation-2">
        <v-card-title class="primary--text pb-2">
            <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                    <v-icon left color="primary" size="28">mdi-calculator-variant</v-icon>
                    <div>
                        <div class="text-h5 font-weight-bold">Kalkulátor investície (VZOR pre investorov)</div>
                        <div class="text-caption grey--text">Naplánuj si svoju investíciu a pozri návratnosť</div>
                    </div>
                </div>
            </div>
        </v-card-title>

        <v-card-text class="pb-2">
            <!-- Quick stats -->
            <calculator-stats :stats="quickStats" />

            <v-row>
                <!-- Ľavý panel - Nastavenia -->
                <v-col cols="12" lg="7">
                    <v-card outlined class="pa-4">
                        <div class="d-flex align-center mb-4">
                            <v-icon left color="primary">mdi-tune</v-icon>
                            <span class="text-subtitle-1 font-weight-medium">Parametre investície</span>
                        </div>

                        <!-- Suma investície s real-time feedback -->
                        <calculator-input v-model="investmentAmount" :presets="investmentPresets"
                            :rules="investmentRules" :has-error="hasInvestmentError" :is-valid="isValidInvestment"
                            :error-message="investmentErrorMessage" @input="calculateResults"
                            @quick-invest="quickInvest" />

                        <!-- Aktuálny koeficient displej -->
                        <v-card outlined :color="coefficientCard.color" class="pa-4 mb-4">
                            <div class="d-flex align-center justify-space-between">
                                <div>
                                    <div class="text-overline">Koeficient násobku vkladu</div>
                                    <div class="text-h3 font-weight-bold" :class="coefficientCard.textColor">
                                        {{ displayCoefficient }}
                                    </div>
                                    <div class="text-caption">
                                        <span v-if="availableAtCurrentCoefficient > 0">
                                            Ešte môžeš investovať: <strong>{{
                                                formatCurrency(availableAtCurrentCoefficient) }}</strong>
                                        </span>
                                        <span v-else class="warning--text">Koeficient bude znížený</span>
                                    </div>
                                </div>
                                <div class="text-right">
                                    <div class="text-overline">Moja účasť na investícií celkom</div>
                                    <div class="text-h5 success--text font-weight-bold">
                                        {{ formatCurrency(payoutClaim) }}
                                    </div>
                                    <div class="text-caption">
                                        Investícia + benefit
                                    </div>
                                </div>
                            </div>

                            <!-- Progress bar pre koeficient -->
                            <v-expand-transition>
                                <div v-if="availableAtCurrentCoefficient > 0" class="mt-3">
                                    <div class="d-flex align-center justify-space-between mb-1">
                                        <span class="text-caption">Využitie koeficientu {{ getCurrentSimpleCoefficient
                                        }}x</span>
                                        <span class="text-caption">{{ coefficientUtilization.toFixed(1) }}%</span>
                                    </div>
                                    <v-progress-linear :value="coefficientUtilization" height="6" rounded
                                        :color="coefficientProgressColor"
                                        background-color="grey lighten-3"></v-progress-linear>
                                </div>
                            </v-expand-transition>

                            <!-- Multi-contract breakdown -->
                            <v-expand-transition>
                                <div v-if="multipleContracts" class="mt-3">
                                    <v-divider class="mb-3"></v-divider>
                                    <div class="text-caption mb-2">
                                        <v-icon left small color="info">mdi-file-multiple</v-icon>
                                        Rozdelenie na {{ investmentBreakdown.length }} zmluvy:
                                    </div>
                                    <div v-for="(part, index) in investmentBreakdown" :key="index"
                                        class="d-flex justify-space-between align-center mb-1">
                                        <span class="text-caption">
                                            {{ formatCurrency(part.amount) }} × {{ part.coefficient }}x
                                        </span>
                                        <strong class="success--text text-caption">
                                            {{ formatCurrency(part.payout) }}
                                        </strong>
                                    </div>
                                </div>
                            </v-expand-transition>
                        </v-card>

                        <!-- Advanced settings -->
                        <v-expansion-panels flat>
                            <v-expansion-panel>
                                <v-expansion-panel-header class="pa-0">
                                    <div class="d-flex align-center">
                                        <v-icon left small>mdi-cog</v-icon>
                                        <span class="text-body-2">Pokročilé nastavenia</span>
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content class="px-0">
                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model.number="annualRevenue"
                                                label="Modelácia predpokladanej tržby ročne" type="number" suffix="€"
                                                outlined dense hide-details @input="debounceCalculate"
                                                @focus="$event.target.select()">
                                                <template v-slot:append-outer>
                                                    <v-btn icon small @click="resetToDefault">
                                                        <v-icon small>mdi-refresh</v-icon>
                                                    </v-btn>
                                                </template>
                                            </v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <div class="d-flex align-center">
                                                <v-slider v-model="payoutPercentage" min="50" max="70" step="1"
                                                    hide-details dense @input="calculateResults">
                                                    <template v-slot:prepend>
                                                        <span class="text-caption">{{ payoutPercentage }}%</span>
                                                    </template>
                                                </v-slider>
                                            </div>
                                            <div class="text-caption grey--text">Koeficient platby z tržieb v %</div>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-card>
                </v-col>

                <!-- Pravý panel - Výsledky -->
                <v-col cols="12" lg="5">
                    <calculator-results :payback-period="paybackPeriod" :my-annual-payout="myAnnualPayout"
                        :my-participation-percentage="myParticipationPercentage" :investment-amount="investmentAmount"
                        :yearly-balance="yearlyBalance" :yearly-balance-class="yearlyBalanceClass"
                        :yearly-balance-with-benefit="yearlyBalanceWithBenefit"
                        :yearly-balance-with-benefit-class="yearlyBalanceWithBenefitClass" :payout-claim="payoutClaim"
                        :total-real-investments="totalInvestments"
                        :total-investments-including-mine="totalInvestmentsIncludingMine"
                        :total-payout-claim="totalPayoutClaim" />
                </v-col>
            </v-row>

            <!-- Action buttons -->
            <v-row class="mt-4">
                <v-col cols="12" class="action-buttons">
                    <v-btn x-large color="success" :disabled="!isValidInvestment" :loading="loading" @click="invest"
                        class="action-btn action-btn--invest px-8" elevation="2">
                        <v-icon left>mdi-trending-up</v-icon>
                        Idem investovať {{ formatCurrency(investmentAmount) }}
                    </v-btn>

                    <v-btn x-large color="primary" outlined @click="showContract" :disabled="!isValidInvestment"
                        class="action-btn action-btn--contract px-6">
                        <v-icon left>mdi-file-document</v-icon>
                        Náhľad mojej budúcej zmluvy
                    </v-btn>
                </v-col>
            </v-row>

            <!-- Informácie -->
            <v-row class="mt-6">
                <v-col cols="12">
                    <v-expansion-panels flat>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                <div class="d-flex align-center">
                                    <v-icon left color="warning">mdi-information</v-icon>
                                    <span class="font-weight-medium">Dôležité informácie</span>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 mb-2">📋 Pravidlá investície:</div>
                                        <ul class="text-body-2 ml-4">
                                            <li>Minimálna investícia: <strong>{{ minInvestment }}€</strong></li>
                                            <li>Len násobky <strong>{{ investmentStep }}€</strong></li>
                                            <li>Maximum: <strong>{{ maxInvestmentAmount }}€</strong></li>
                                            <li>Číslo zmluvy: <strong>ID/Poradové</strong></li>
                                        </ul>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 mb-2">⚡ Proces aktivácie:</div>
                                        <ul class="text-body-2 ml-4">
                                            <li>Platnosť po <strong>pripísaní na účet</strong></li>
                                            <li>Spracovanie: <strong>až 1 deň</strong></li>
                                            <li>Údaje sú <strong>informatívne</strong></li>
                                            <li>Možnosť <strong>zastavenia príjmu</strong> investícií</li>
                                        </ul>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
import { debounce } from 'lodash';
import {
    calculateInvestmentBreakdown,
    calculatePayoutClaim,
    formatCurrency
} from '@/utils/investment-math';
import {
    MIN_INVESTMENT,
    MAX_INVESTMENT,
    INVESTMENT_STEP,
    DEFAULT_ANNUAL_REVENUE,
    DEFAULT_PAYOUT_PERCENTAGE,
    PAYOUT_PERCENTAGE_MIN,
    PAYOUT_PERCENTAGE_MAX,
    COEFFICIENT_LEVELS,
    INVESTMENT_PRESETS
} from '@/config/investment';

import CalculatorInput from './calculator/CalculatorInput.vue';
import CalculatorStats from './calculator/CalculatorStats.vue';
import CalculatorResults from './calculator/CalculatorResults.vue';

export default {
    name: "InvestmentCalculator",

    components: {
        CalculatorInput,
        CalculatorStats,
        CalculatorResults
    },

    props: {
        defaultAnnualRevenue: { type: Number, default: DEFAULT_ANNUAL_REVENUE },
        totalInvestments: { type: Number, default: 0 },
        maxInvestmentAmount: { type: Number, default: MAX_INVESTMENT },
        myInactiveInvestmentsTotal: { type: Number, default: 0 },
        activeInvestmentsTotal: { type: Number, default: null },
        defaultPayoutPercentage: { type: Number, default: DEFAULT_PAYOUT_PERCENTAGE },
        totalPayoutClaim: { type: Number, default: 0 }
    },

    data() {
        return {
            debounceCalculate: 0,
            investmentAmount: MIN_INVESTMENT,
            annualRevenue: this.defaultAnnualRevenue,
            payoutPercentage: this.defaultPayoutPercentage,
            loading: false,

            investmentPresets: INVESTMENT_PRESETS,

            // Constants exposed for template binding
            minInvestment: MIN_INVESTMENT,
            investmentStep: INVESTMENT_STEP,
            payoutPercentageMin: PAYOUT_PERCENTAGE_MIN,
            payoutPercentageMax: PAYOUT_PERCENTAGE_MAX
        };
    },

    computed: {
        // Validation
        investmentRules() {
            const min = MIN_INVESTMENT;
            const max = MAX_INVESTMENT;
            const step = INVESTMENT_STEP;
            return [
                v => !!v || 'Suma je povinná',
                v => v >= min || `Minimálna investícia je ${min}€`,
                v => v % step === 0 || `Len násobky ${step}€`,
                v => v <= max || `Maximum ${max}€`
            ];
        },

        hasInvestmentError() {
            return this.investmentAmount > 0 && !this.isValidInvestment;
        },

        investmentErrorMessage() {
            if (this.investmentAmount < MIN_INVESTMENT) return `Minimálna investícia je ${MIN_INVESTMENT}€`;
            if (this.investmentAmount % INVESTMENT_STEP !== 0) return `Investovať môžete len násobky ${INVESTMENT_STEP}€`;
            if (this.investmentAmount > MAX_INVESTMENT) return `Maximálna investícia je ${MAX_INVESTMENT}€`;
            return '';
        },

        isValidInvestment() {
            return this.investmentAmount >= MIN_INVESTMENT &&
                this.investmentAmount % INVESTMENT_STEP === 0 &&
                this.investmentAmount <= MAX_INVESTMENT;
        },

        // Core calculations
        totalInvestmentsIncludingMine() {
            return this.totalInvestments + (this.myInactiveInvestmentsTotal || 0);
        },

        coefficientBaseTotal() {
            if (Number.isFinite(Number(this.totalInvestments))) {
                return Number(this.totalInvestments) + Number(this.myInactiveInvestmentsTotal || 0);
            }
            return Number(this.totalInvestments || 0);
        },

        coefficientTotalWithCurrentInvestment() {
            return this.coefficientBaseTotal + this.investmentAmount;
        },

        investmentBreakdown() {
            return calculateInvestmentBreakdown(
                this.investmentAmount,
                this.coefficientBaseTotal,
                COEFFICIENT_LEVELS
            );
        },

        revenueForPayouts() {
            return this.annualRevenue * (this.payoutPercentage / 100);
        },

        payoutClaim() {
            return calculatePayoutClaim(this.investmentBreakdown);
        },

        myAnnualPayout() {
            return this.revenueForPayouts * (this.investmentAmount / this.totalInvestmentsIncludingMine);
        },

        // Display helpers
        displayCoefficient() {
            const breakdown = this.investmentBreakdown;
            if (breakdown.length === 1) {
                return breakdown[0].coefficient + 'x';
            }
            if (breakdown.length === 0) return '1.5x'; // Fallback matching original "Mix" logic if empty? 
            // Original code: if length === 1 return coeff, else 'Mix'.
            // If 0? 'Mix'.
            return 'Mix';
        },

        getCurrentSimpleCoefficient() {
            const currentTotal = this.coefficientTotalWithCurrentInvestment;
            for (let level of COEFFICIENT_LEVELS) {
                if (currentTotal < level.threshold) {
                    return level.coefficient;
                }
            }
            return COEFFICIENT_LEVELS[COEFFICIENT_LEVELS.length - 1].coefficient;
        },

        multipleContracts() {
            return this.investmentBreakdown.length > 1;
        },

        // Availability
        availableAtCurrentCoefficient() {
            const currentTotal = this.coefficientTotalWithCurrentInvestment;
            for (let level of COEFFICIENT_LEVELS) {
                if (currentTotal < level.threshold) {
                    return level.threshold - currentTotal;
                }
            }
            return 0;
        },

        coefficientUtilization() {
            const currentCoefficient = this.getCurrentSimpleCoefficient;
            const currentLevelIndex = COEFFICIENT_LEVELS.findIndex(l => l.coefficient === currentCoefficient);
            const currentLevelThreshold = currentLevelIndex >= 0
                ? COEFFICIENT_LEVELS[currentLevelIndex].threshold
                : 0;
            const previousThreshold = currentLevelIndex > 0
                ? COEFFICIENT_LEVELS[currentLevelIndex - 1].threshold
                : 0;
            const levelSize = currentLevelThreshold - previousThreshold;
            const usedInLevel = this.coefficientTotalWithCurrentInvestment - previousThreshold;
            if (levelSize <= 0) return 100; // Prevention
            return Math.max(0, Math.min(100, (usedInLevel / levelSize) * 100));
        },

        // Returns and performance
        yearlyBalance() {
            return this.myAnnualPayout - this.investmentAmount;
        },

        yearlyBalanceWithBenefit() {
            return this.myAnnualPayout - this.payoutClaim;
        },

        paybackPeriod() {
            if (this.myAnnualPayout <= 0) return 'Nikdy';
            const years = this.investmentAmount / this.myAnnualPayout;
            if (years > 10) return '10+ rokov';
            if (years > 1) return `${years.toFixed(1)} rokov`;
            const months = years * 12;
            return `${Math.ceil(months)} mesiacov`;
        },

        myParticipationPercentage() {
            return ((this.investmentAmount / this.totalInvestmentsIncludingMine) * 100).toFixed(4);
        },

        quickStats() {
            return [
                {
                    label: 'Nárok na vrátenie',
                    value: this.formatCurrency(this.payoutClaim),
                    color: 'success'
                },
                {
                    label: 'Návrat za',
                    value: this.paybackPeriod,
                    color: 'info'
                },
                {
                    label: 'Koeficient',
                    value: this.displayCoefficient,
                    color: 'primary'
                }
            ];
        },

        coefficientCard() {
            const coeff = this.getCurrentSimpleCoefficient;
            const colors = {
                5: { color: 'success lighten-5', textColor: 'success--text' },
                4: { color: 'warning lighten-5', textColor: 'warning--text' },
                3: { color: 'orange lighten-5', textColor: 'orange--text' },
                2: { color: 'error lighten-5', textColor: 'error--text' },
                1.5: { color: 'grey lighten-3', textColor: 'grey--text text--darken-2' }
            };
            return colors[coeff] || colors[1.5];
        },

        coefficientProgressColor() {
            const coeff = this.getCurrentSimpleCoefficient;
            if (coeff >= 5) return 'success';
            if (coeff >= 4) return 'warning';
            if (coeff >= 3) return 'orange';
            return 'error';
        },

        yearlyBalanceClass() {
            return this.yearlyBalance >= 0 ? 'success--text' : 'error--text';
        },

        yearlyBalanceWithBenefitClass() {
            return this.yearlyBalanceWithBenefit >= 0 ? 'success--text' : 'error--text';
        },
    },

    watch: {
        defaultAnnualRevenue(newVal) {
            this.annualRevenue = newVal;
            this.calculateResults();
        },

        defaultPayoutPercentage(newVal) {
            const parsed = Number(newVal);
            if (!Number.isFinite(parsed) || parsed === this.payoutPercentage) {
                return;
            }

            this.payoutPercentage = parsed;
            this.calculateResults();
        }
    },

    mounted() {
        this.calculateResults();
        // Setup debounced calculation
        this.debounceCalculate = debounce(this.calculateResults, 300);
    },

    methods: {
        formatCurrency,  // Imported utility

        calculateResults() {
            this.$emit('annual-revenue-change', this.annualRevenue);
            this.$emit('payout-percentage-change', this.payoutPercentage);
        },

        resetToDefault() {
            this.annualRevenue = this.defaultAnnualRevenue;
            this.calculateResults();
        },

        quickInvest() {
            if (this.isValidInvestment) {
                this.invest();
            }
        },

        invest() {
            if (!this.isValidInvestment) return;

            this.loading = true;

            // Simulate API call delay
            setTimeout(() => {
                this.$emit('invest', this.investmentAmount);
                this.loading = false;
            }, 500);
        },

        showContract() {
            this.$emit('show-contract-preview', {
                amount: this.investmentAmount,
                coefficient: this.displayCoefficient,
                breakdown: this.investmentBreakdown,
                payoutClaim: this.payoutClaim
            });
        }
    }
};
</script>

<style scoped>
/* Keeping original styles */
.investment-calculator {
    margin-bottom: 24px;
    transition: all 0.3s ease;
}

.v-btn--x-large {
    height: 56px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
}

.action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
}

.action-btn {
    max-width: 100%;
    text-transform: none !important;
}

/* Responsive improvements */
@media (max-width: 960px) {
    .v-btn--x-large {
        height: 48px !important;
        font-size: 14px !important;
    }

    .text-h3 {
        font-size: 1.8rem !important;
    }

    .text-h5 {
        font-size: 1.3rem !important;
    }
}

@media (max-width: 600px) {
    .v-card-title {
        padding: 16px 16px 8px 16px;
    }

    .v-card-text {
        padding: 8px 16px 16px 16px;
    }

    .text-h6 {
        font-size: 1rem !important;
    }

    .action-buttons {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .action-btn {
        width: 100%;
        margin-right: 0 !important;
    }

    ::v-deep .action-btn .v-btn__content {
        white-space: normal;
        text-align: center;
        line-height: 1.2;
    }

    .action-btn--contract {
        font-size: 13px !important;
        letter-spacing: 0.02em !important;
    }
}
</style>
